import { Component, OnInit, ViewChild } from '@angular/core';

import { ArkUnitOfWork, AuthService, AppInformationService } from '../../core/core.module';
import { CustomStore, BreezeDxStoreService } from 'app/core/core.module';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { SharedModule } from 'app/shared/shared.module';
import { confirm, alert } from 'devextreme/ui/dialog';
import { DevExtremeModule, DxDataGridComponent } from 'devextreme-angular';
import { Predicate } from 'breeze-client';
import moment from 'moment';
import { MessageAttachment } from 'app/core/model/entity-model';
import { SentsDialogComponent } from '../sents-dialog.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';

@Component({
    selector: 'app-messages-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    standalone: true,
    imports: [DevExtremeModule, SharedModule, RouterLink, DxDataGridComponent],
})
export class ListComponent implements OnInit {
    store: CustomStore;
    mode: string;
    showAll: boolean;
    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    constructor(
        private uow: ArkUnitOfWork,
        private storeService: BreezeDxStoreService,
        private route: ActivatedRoute,
        private router: Router,
        public auth: AuthService,
        public appInfo: AppInformationService,
        private modalService: BsModalService,
    ) {}

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'before',
            },
        );
    }

    async ngOnInit() {
        this.mode = this.route.snapshot.data['mode'];
        this.showAll = false;
        const delay = await this.uow.people.byId(1);
        this.getStore();
    }

    getStore() {
        let predicate = new Predicate('deleted', '==', null).and('sent', this.mode === 'drafts' ? '==' : '!=', null);
        if (!this.auth.hasRole('Administrator') && this.appInfo.graafschap) predicate = predicate.and('fromUserId', '==', this.auth.currentUser.id);
        let query = this.uow.messages.baseQuery().where(predicate).expand('toGroup, fromUser');
        if (this.mode != 'drafts' && !this.showAll) query = query.take(100);
        this.store = this.storeService.createStore(this.uow, this.uow.messages, query, 'id', 'created desc');
    }

    add() {
        this.router.navigate(['new'], { relativeTo: this.route });
    }

    async getInput(prompt: string, mode: string, array?: any[], value?: any): Promise<any> {
        let modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Graafschap Church',
                dialogPrompt: prompt,
                dialogValueArray: array,
                dialogValue: value,
                mode: mode,
            },
        });
        const promise = new Promise<any>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.success) {
                    resolve(result.value);
                }
                modalRef.hide();
                resolve(null);
            });
        });
        return promise;
    }

    async copy() {
        const dialogResult = await (<Promise<boolean>>confirm('Do you want to make a draft copy of this message?', 'Copy Message'));
        if (!dialogResult) return;

        const message = await this.uow.messages.byId(this.dataGrid.instance.getSelectedRowsData()[0].id, 'attachments');

        const announcements = message.subject.toLowerCase().indexOf('announcement') > 0;
        const prayerEmail = message.subject.toLowerCase().indexOf('prayer') > 0;
        let newEmailSubject = '';
        if (announcements || prayerEmail) {
            const today = new Date();
            if (announcements) {
                const sunday = moment(today).isoWeekday(7).format('MMMM DD');
                newEmailSubject = `${sunday} Announcements`;
                newEmailSubject = await this.getInput('New Message Subject', 'text', null, newEmailSubject);
            }
            if (prayerEmail) {
                const wednesday = moment(today).isoWeekday(3).format('MMMM DD');
                newEmailSubject = `${wednesday} Prayer Email`;
                newEmailSubject = await this.getInput('New Message Subject', 'text', null, newEmailSubject);
            }
        }

        this.appInfo.pleaseWait = true;

        const newMessage = this.uow.messages.createEntity();
        newMessage.created = moment().toDate();
        newMessage.subject = !!newEmailSubject && newEmailSubject !== '' ? newEmailSubject : message.subject + ' - copy';
        newMessage.body = message.body;
        newMessage.toGroupId = message.toGroupId;
        newMessage.fromUserId = this.auth.currentUser.id;
        message.attachments.forEach((a) => {
            const newAttachment = this.uow.manager.createEntity('MessageAttachment') as MessageAttachment;
            newAttachment.name = a.name;
            newAttachment.fileName = a.fileName;
            newAttachment.message = newMessage;
        });
        await this.uow.commit();
        this.dataGrid.instance.clearSelection();
        this.dataGrid.instance.option('focusedRowIndex', -1);
        this.appInfo.pleaseWait = false;
        this.router.navigate(['../drafts/' + newMessage.id], { relativeTo: this.route });
    }

    async delete() {
        if (this.dataGrid.focusedRowIndex === -1) return;
        const dialogResult = await (<Promise<boolean>>(
            confirm(`Are you sure you want to delete ${this.dataGrid.instance.getSelectedRowsData().length === 1 ? 'this draft' : 'these drafts'}?`, `${this.appInfo.systemName}`)
        ));

        if (!dialogResult) return;

        this.appInfo.pleaseWaitShow();
        this.dataGrid.instance.getSelectedRowsData().forEach((messageInfo) => {
            messageInfo.deleted = new Date();
        });

        // const message = await this.uow.messages.byId(this.dataGrid.focusedRowKey);
        // message.deleted = new Date();
        await this.uow.commit();
        this.dataGrid.instance.clearSelection();
        this.dataGrid.instance.option('focusedRowIndex', -1);
        this.getStore();
        this.appInfo.pleaseWaitHide();
    }

    async openSents() {
        const message = await this.uow.messages.byId(this.dataGrid.instance.getSelectedRowsData()[0].id);
        const modalRef = this.modalService.show(SentsDialogComponent, {
            class: 'modal-mdw',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                messageId: this.dataGrid.instance.getSelectedRowsData()[0].id,
                mode: 'email',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result) {
            }
        });
    }

    async toggleShowAll() {
        this.showAll = !this.showAll;
        await this.getStore();
    }
}
